/**
 * RecentTxList — list of transactions grouped by day with sticky day headers.
 *
 * Per-row formatting:
 *   - category badge + name + note
 *   - main amount on right, FX-converted into the primary currency
 *     (e.g. JPY 1000 with TWD primary → "-NT$ 214")
 *   - 「原幣 ¥ 1,000」 sub-line shown only when row.currency ≠ primary
 *   - amount color-coded by kind (`--income` for income, `--fg-1` for expense)
 *
 * Day subtotals + monthly subtotal pill render in the primary currency
 * (single line). Foreign-currency entries are FX-converted via
 * `convertMinor`.
 *
 * Empty state: 「本月還沒有紀錄」.
 */

import { useMemo, useState } from 'react';
import { cssColor } from '@/lib/colorToken';
import type { CategoryRow, TransactionRow } from '@/lib/sheets/types';
import { formatAmount } from '@/lib/format/currency';
import { convertMinor } from '@/lib/format/fx';
import { displayTxName } from '@/lib/displayTxName';
import { displayTxIcon } from '@/lib/displayTxIcon';
import Icon, { type IconName } from '@/components/common/Icon';
import { useStore } from '@/state/store';
import { timePart } from '@/lib/format/datetime';
import BottomSheet from '@/components/common/BottomSheet';
import { haptic } from '@/lib/haptics';

export interface RecentTxListProps {
  transactions: TransactionRow[];
  categories: CategoryRow[];
  /** Optional cap on rows shown. */
  limit?: number;
  onTxClick?: (tx: TransactionRow) => void;
  /** Invoked after the user swipes a row past the delete threshold. */
  onDelete?: (tx: TransactionRow) => void;
  /** Primary currency — used to decide whether to render the 「原幣 ¥1,200」
   *  suffix line on a row whose `currency` differs from the primary. */
  primaryCurrency?: string;
}

interface DayGroup {
  date: string;
  items: TransactionRow[];
}

function groupByDate(items: TransactionRow[]): DayGroup[] {
  const map = new Map<string, TransactionRow[]>();
  for (const t of items) {
    // v5: bucket strictly by the day part — without this, an imported
    // row with `2025-04-30T11:51` and a legacy row with `2025-04-30`
    // land under different keys and the day section visually splits.
    const day = timePart(t.date) ? t.date.slice(0, 10) : t.date;
    const list = map.get(day);
    if (list) list.push(t);
    else map.set(day, [t]);
  }
  // Sort: newest day first.
  return [...map.entries()]
    .sort((a, b) => (a[0] < b[0] ? 1 : a[0] > b[0] ? -1 : 0))
    .map(([date, list]) => ({ date, items: list }));
}

/**
 * Sum a list of txs to a single primary-currency total, FX-converting
 * each foreign-currency entry. Returns one entry — used by both the
 * monthly subtotal pill and the per-day in/out chip row.
 */
function sumInPrimary(
  items: TransactionRow[],
  primary: string,
): { expenseMinor: number; incomeMinor: number } {
  let expenseMinor = 0;
  let incomeMinor = 0;
  for (const t of items) {
    const minor = convertMinor(t.amount_minor, t.currency || 'TWD', primary);
    if (t.kind === 'income') incomeMinor += minor;
    else expenseMinor += minor;
  }
  return { expenseMinor, incomeMinor };
}

interface DayHeaderParts {
  monthDay: string; // "5/2"
  weekday: string;  // "週六"
}

function formatDayHeaderParts(dateStr: string): DayHeaderParts {
  // v5: input may be `YYYY-MM-DD` or `YYYY-MM-DDTHH:MM`. Strip to the
  // day part so the regex anchor still matches and the user never sees
  // a literal `T` in the day header (UX bug — looked like raw debug
  // output to non-technical users).
  const day = dateStr.length > 10 && dateStr[10] === 'T'
    ? dateStr.slice(0, 10)
    : dateStr;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return { monthDay: day, weekday: '' };
  const dt = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const wd = ['日', '一', '二', '三', '四', '五', '六'][dt.getDay()];
  return {
    monthDay: `${dt.getMonth() + 1}/${dt.getDate()}`,
    weekday: `週${wd}`,
  };
}

/**
 * Day in/out totals, FX-converted into the primary currency. One entry —
 * mixed currencies are summed under the primary.
 */
interface DaySumPrimary {
  inMinor: number;
  outMinor: number;
}

function dayTotalsInPrimary(
  items: TransactionRow[],
  primary: string,
): DaySumPrimary {
  let inMinor = 0;
  let outMinor = 0;
  for (const t of items) {
    const minor = convertMinor(t.amount_minor, t.currency || 'TWD', primary);
    if (t.kind === 'income') inMinor += minor;
    else outMinor += minor;
  }
  return { inMinor, outMinor };
}

export default function RecentTxList({
  transactions,
  categories,
  limit,
  onTxClick,
  onDelete,
  primaryCurrency,
}: RecentTxListProps): JSX.Element {
  const items = useMemo(
    () => (limit && limit > 0 ? transactions.slice(0, limit) : transactions),
    [transactions, limit],
  );

  const primary = (primaryCurrency || 'TWD').toUpperCase();
  const subtotal = useMemo(() => sumInPrimary(items, primary), [items, primary]);
  const groups = useMemo(() => groupByDate(items), [items]);

  const catById = useMemo(() => {
    const m = new Map<string, CategoryRow>();
    for (const c of categories) m.set(c.id, c);
    return m;
  }, [categories]);

  // Set of transaction ids with a pending sync op so we can stamp 「待同步」.
  // Selector returns the raw `pending` array (referentially stable across
  // renders unless the queue actually changed) — we derive the Set with
  // `useMemo` to avoid the "selector returns new object" Zustand pitfall.
  const pending = useStore((s) => s.sync.pending);
  const pendingTxIds = useMemo(() => {
    const set = new Set<string>();
    for (const op of pending) {
      if (
        op.type === 'create_tx' ||
        op.type === 'update_tx' ||
        op.type === 'delete_tx'
      ) {
        const p = op.payload as { id?: string } | null | undefined;
        if (p && typeof p.id === 'string') set.add(p.id);
      }
    }
    return set;
  }, [pending]);

  if (items.length === 0) {
    return (
      <div
        style={{
          padding: '32px 20px',
          textAlign: 'center',
          color: 'var(--fg-3)',
          font: '500 14px var(--font-cjk)',
        }}
      >
        本月還沒有紀錄
      </div>
    );
  }

  return (
    <div>
      {/* Single subtotal pill in primary currency (FX-converted). */}
      <div
        data-testid="rtl-subtotals"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          padding: '4px 4px 12px',
          font: '500 12px var(--font-num)',
          color: 'var(--fg-2)',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'baseline',
            gap: 6,
            padding: '4px 10px',
            background: 'var(--bg-raised)',
            borderRadius: 999,
          }}
        >
          <span
            style={{
              font: '500 11px var(--font-cjk)',
              color: 'var(--fg-3)',
              letterSpacing: '0.04em',
            }}
          >
            {primary}
          </span>
          {subtotal.expenseMinor > 0 && (
            <span style={{ color: 'var(--fg-1)' }}>
              -{formatAmount(subtotal.expenseMinor, primary)}
            </span>
          )}
          {subtotal.incomeMinor > 0 && (
            <span style={{ color: 'var(--income)' }}>
              +{formatAmount(subtotal.incomeMinor, primary)}
            </span>
          )}
        </div>
      </div>

      {groups.map((g) => {
        const parts = formatDayHeaderParts(g.date);
        const dayTotals = dayTotalsInPrimary(g.items, primary);
        return (
          <div key={g.date} style={{ marginBottom: 14 }}>
            <div
              style={{
                position: 'sticky',
                top: 0,
                // Bumped to 2: sibling TxRow buttons live at z-index 1 with
                // position:relative for swipe-delete. Equal z-index made the
                // button win source-order and paint over the sticky day
                // header's left edge, leaving "5/5 週二" half-clipped by the
                // rounded card corner above the row.
                zIndex: 2,
                background: 'var(--cream-50)',
                padding: '6px 4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 8,
              }}
            >
              <div
                style={{
                  font: '500 13px var(--font-cjk)',
                  color: 'var(--fg-2)',
                }}
              >
                {parts.monthDay}{' '}
                <span style={{ color: 'var(--fg-3)', fontWeight: 400 }}>
                  {parts.weekday}
                </span>
              </div>
              <div
                style={{
                  display: 'inline-flex',
                  gap: 6,
                  // 11px instead of 12px so income + expense fit on one line
                  // even at 6-digit amounts (NT$ 113,787 + −NT$ 20,420). The
                  // wrap broke the sticky-header layout on the all-records
                  // screen — flex children have to be flexShrink:0 + nowrap
                  // here, otherwise the right rail dropped to a second line
                  // and shoved the day grouping down.
                  font: '500 11px var(--font-num)',
                  fontVariantNumeric: 'tabular-nums',
                  color: 'var(--fg-3)',
                  flexShrink: 0,
                  whiteSpace: 'nowrap',
                }}
              >
                {dayTotals.inMinor > 0 && (
                  <span style={{ color: 'var(--income)' }}>
                    +{formatAmount(dayTotals.inMinor, primary)}
                  </span>
                )}
                {dayTotals.outMinor > 0 && (
                  <span>−{formatAmount(dayTotals.outMinor, primary)}</span>
                )}
              </div>
            </div>
            <div
              style={{
                background: 'var(--bg-surface)',
                borderRadius: 16,
                padding: '4px 14px',
                boxShadow: 'var(--shadow-1)',
              }}
            >
              {g.items.map((t, i) => (
                <TxRow
                  key={t.id}
                  tx={t}
                  category={catById.get(t.category_id)}
                  subcategory={t.subcategory_id ? catById.get(t.subcategory_id) : undefined}
                  last={i === g.items.length - 1}
                  pending={pendingTxIds.has(t.id)}
                  {...(primaryCurrency ? { primaryCurrency } : {})}
                  onClick={onTxClick ? () => onTxClick(t) : undefined}
                  onDelete={onDelete ? () => onDelete(t) : undefined}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface TxRowProps {
  tx: TransactionRow;
  category: CategoryRow | undefined;
  /** Sub-category row (matches `tx.subcategory_id`). Undefined when none. */
  subcategory?: CategoryRow | undefined;
  last: boolean;
  pending?: boolean;
  primaryCurrency?: string;
  onClick?: () => void;
  /** Invoked after the user confirms the swipe-delete gesture. */
  onDelete?: () => void;
}

function TxRow({
  tx,
  category,
  subcategory,
  last,
  pending,
  primaryCurrency,
  onClick,
  onDelete,
}: TxRowProps): JSX.Element {
  const color = cssColor(category?.color_token);
  const iconName = displayTxIcon(category, subcategory) as IconName;
  const isInc = tx.kind === 'income';
  const tags = (tx.tags ?? '').split(',').map((s) => s.trim()).filter(Boolean);

  // Display amount: when a primary currency is supplied, FX-convert the row
  // to that currency for the headline figure. Native amount is preserved on
  // the 原幣 sub-line below.
  const displayCurrency = (primaryCurrency || tx.currency || 'TWD').toUpperCase();
  const displayMinor = primaryCurrency
    ? convertMinor(tx.amount_minor, tx.currency || 'TWD', primaryCurrency)
    : tx.amount_minor;
  const primaryName = displayTxName(tx, category, subcategory);
  const ariaLabel = `${primaryName === '—' ? '紀錄' : primaryName}，${isInc ? '收入' : '支出'} ${formatAmount(displayMinor, displayCurrency)}`;

  // Swipe-to-delete state. Pure pointer-event implementation (no library).
  // Drag left → reveal delete affordance behind the row; release past
  // 80px → open a confirmation BottomSheet (NOT auto-delete). The user
  // confirms or cancels there. Earlier the swipe alone was destructive
  // — accidental left-flicks while scrolling could nuke a row with no
  // recovery affordance.
  const [drag, setDrag] = useState(0);
  const [startX, setStartX] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const SWIPE_THRESHOLD = 80;

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (!onDelete) return;
    setStartX(e.clientX);
  };
  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>): void => {
    if (startX == null) return;
    const dx = e.clientX - startX;
    if (dx < 0) setDrag(Math.max(dx, -120)); // only allow left
  };
  const handlePointerUp = (): void => {
    if (drag <= -SWIPE_THRESHOLD && onDelete) {
      setConfirmOpen(true);
    }
    setDrag(0);
    setStartX(null);
  };

  return (
    <div
      style={{
        position: 'relative',
        borderBottom: last ? '0' : '0.5px solid var(--border-1)',
      }}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      {onDelete && drag < -8 && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            bottom: 0,
            width: Math.abs(drag),
            background: 'var(--danger)',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            font: '500 13px var(--font-cjk)',
            paddingRight: 12,
          }}
        >
          {drag <= -SWIPE_THRESHOLD ? '放開以刪除' : '刪除'}
        </div>
      )}
    <button
      type="button"
      aria-label={ariaLabel}
      onClick={
        onClick
          ? () => {
              haptic('tap');
              onClick();
            }
          : undefined
      }
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '12px 0',
        width: '100%',
        // Was --bg-app (cream) which clashed with the parent card's
        // --bg-surface (now #FDFAF4) — produced a visible cream rectangle
        // inside the card. Use the surface tint so the row blends with
        // the card and the swipe-delete reveal still hides cleanly.
        background: 'var(--bg-surface)',
        border: 0,
        cursor: onClick ? 'pointer' : 'default',
        textAlign: 'left',
        transform: `translateX(${drag}px)`,
        transition: drag === 0 ? 'transform 200ms var(--ease-out)' : 'none',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <div
        style={{
          width: 38,
          height: 38,
          borderRadius: 12,
          background: color,
          display: 'grid',
          placeItems: 'center',
          color: '#fff',
          flexShrink: 0,
        }}
      >
        <Icon name={iconName} size={19} stroke="#fff" />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            font: '500 15px var(--font-cjk)',
            color: 'var(--fg-1)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {primaryName}
        </div>
        <div
          style={{
            display: 'flex',
            gap: 6,
            alignItems: 'center',
            marginTop: 2,
          }}
        >
          <span style={{ font: '400 12px var(--font-cjk)', color: 'var(--fg-3)' }}>
            {category?.name ?? ''}
            {subcategory ? ` · ${subcategory.name}` : ''}
            {timePart(tx.date) ? ` · ${timePart(tx.date)}` : ''}
          </span>
          {tags.map((t) => (
            <span
              key={t}
              style={{
                font: '500 10px var(--font-cjk)',
                color: 'var(--honey-500)',
                background: 'rgba(199,154,75,0.14)',
                padding: '1px 6px',
                borderRadius: 999,
              }}
            >
              {t}
            </span>
          ))}
          {tx.recurring_id && (
            <span
              style={{
                font: '500 10px var(--font-cjk)',
                color: 'var(--matcha-700)',
                background: 'rgba(126,139,90,0.14)',
                padding: '1px 6px',
                borderRadius: 999,
              }}
            >
              固定
            </span>
          )}
          {pending && (
            <span
              data-testid="tx-pending-badge"
              style={{
                font: '500 10px var(--font-cjk)',
                color: 'var(--honey-700, var(--honey-500))',
                background: 'rgba(199,154,75,0.18)',
                padding: '1px 6px',
                borderRadius: 999,
              }}
            >
              待同步
            </span>
          )}
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div
          style={{
            font: '600 16px var(--font-num)',
            fontVariantNumeric: 'tabular-nums',
            color: isInc ? 'var(--income)' : 'var(--fg-1)',
          }}
        >
          {isInc ? '+' : '-'}
          {formatAmount(displayMinor, displayCurrency)}
        </div>
        {primaryCurrency &&
          tx.currency &&
          tx.currency.toUpperCase() !== primaryCurrency.toUpperCase() && (
            <div
              data-testid="tx-original-currency"
              style={{
                font: '400 11px var(--font-num)',
                fontVariantNumeric: 'tabular-nums',
                color: 'var(--fg-3)',
                marginTop: 2,
              }}
            >
              原幣 {formatAmount(tx.amount_minor, tx.currency)}
            </div>
          )}
      </div>
    </button>
    {/* Confirmation sheet — shown after a swipe past threshold or any
        future intentional delete trigger. Mirrors the AddTxScreen edit-
        mode confirm so the UX is consistent. */}
    {onDelete && (
      <BottomSheet
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="刪除這筆紀錄？"
      >
        <div
          style={{
            font: '400 14px var(--font-cjk)',
            color: 'var(--fg-2)',
            paddingBottom: 16,
          }}
        >
          {primaryName === '—' ? '紀錄' : primaryName} ·
          {' '}
          {formatAmount(tx.amount_minor, tx.currency)}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={() => setConfirmOpen(false)}
            style={{
              flex: 1,
              padding: 12,
              background: 'var(--bg-raised)',
              color: 'var(--fg-1)',
              border: 0,
              borderRadius: 12,
              font: '500 14px var(--font-cjk)',
              cursor: 'pointer',
            }}
          >
            取消
          </button>
          <button
            type="button"
            onClick={() => {
              setConfirmOpen(false);
              onDelete?.();
            }}
            style={{
              flex: 1,
              padding: 12,
              background: 'var(--danger)',
              color: '#fff',
              border: 0,
              borderRadius: 12,
              font: '600 14px var(--font-cjk)',
              cursor: 'pointer',
            }}
          >
            確定刪除
          </button>
        </div>
      </BottomSheet>
    )}
    </div>
  );
}
